import { cn } from '@/lib/utils/cn.js';
import { useBrandingStore } from '@/stores/branding.js';

interface BrandLogoProps {
  /** Hide the sitename wordmark and render the chip alone. */
  markOnly?: boolean;
  /** Square size of the gradient chip in px (wordmark scales with it). */
  size?: number;
  className?: string;
}

export function BrandLogo({ markOnly = false, size = 36, className }: BrandLogoProps) {
  const logoUrl = useBrandingStore((s) => s.branding?.logoUrl) ?? null;
  const sitename = useBrandingStore((s) => s.branding?.sitename) ?? '';
  const stroke = Math.max(4, Math.round(size * 0.16));
  return (
    <span className={cn('group inline-flex items-center gap-2.5', className)}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={sitename || 'logo'}
          width={size}
          height={size}
          className="shrink-0 rounded-xl object-contain"
          style={{ width: size, height: size }}
        />
      ) : (
        <span
          className="grid shrink-0 place-items-center rounded-xl bg-gradient-to-br from-[var(--accent)] to-[var(--primary)] shadow-[0_2px_12px_var(--primary-glow)] transition-transform duration-200 group-hover:scale-105"
          style={{ width: size, height: size }}
        >
          <svg width={size * 0.6} height={size * 0.6} viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <g stroke="#ffffff" strokeLinecap="round" strokeLinejoin="round">
              <line x1="14" y1="50" x2="14" y2="32" strokeWidth={stroke} />
              <line x1="50" y1="50" x2="50" y2="32" strokeWidth={stroke} />
              <path d="M14 32 Q32 12 50 32" strokeWidth={stroke} />
            </g>
            <circle cx="32" cy="14" r="3.5" fill="#ffffff" />
          </svg>
        </span>
      )}
      {!markOnly && sitename && (
        <span
          className="max-sm:hidden font-extrabold leading-none tracking-tight text-[var(--text-primary)]"
          style={{ fontSize: size * 0.6 }}
        >
          {sitename}
        </span>
      )}
    </span>
  );
}
